import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share2, Copy, Twitter, Linkedin, Facebook, Send, Check, Pencil } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ShareModal({ open, onClose }) {
    const { user, refresh } = useAuth();
    const [editing, setEditing] = useState(false);
    const [uname, setUname] = useState(user?.username || "");
    const [copied, setCopied] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    React.useEffect(() => {
        setUname(user?.username || "");
    }, [user?.username]);

    const publicUrl = `${window.location.origin}/u/${user?.username || ""}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
        } catch {
            // clipboard permission may not be available in some contexts
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
    };

    const saveHandle = async () => {
        setSaving(true);
        setError(null);
        try {
            await api.post("/profile/username", { username: uname });
            await refresh();
            setEditing(false);
        } catch (e) {
            setError(e.response?.data?.detail || "Could not update username");
        } finally {
            setSaving(false);
        }
    };

    const tweetIntent = () => {
        const text = `I just claimed my Living Ledger on Project Atlas — every merit badge, every contribution, verifiable. ${publicUrl}`;
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
    };

    const linkedInIntent = () => {
        window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`,
            "_blank",
        );
    };

    const facebookIntent = () => {
        window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`,
            "_blank",
        );
    };

    const nativeShare = async () => {
        const shareData = {
            title: `${user?.name || "My"} Living Ledger — Project Atlas`,
            text: `Every merit badge, every beacon, verifiable on Atlas.`,
            url: publicUrl,
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await copy();
            }
        } catch {
            /* user cancelled */
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-md p-4"
                    onClick={onClose}
                    data-testid="share-modal"
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="crystal rounded-3xl max-w-md w-full p-7 relative"
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"
                            data-testid="share-close"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <p
                            className="chip mb-3"
                            style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}
                        >
                            <Share2 className="w-3 h-3" /> Share your ledger
                        </p>
                        <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900">
                            Your Living Resume, public and verifiable.
                        </h2>
                        <p className="text-sm text-slate-600 mt-2">
                            Every badge, every beacon, every contribution — visible at a single un-fakeable URL.
                        </p>

                        <div className="mt-4 rounded-2xl border border-slate-200 bg-white/70 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                                Public URL
                            </div>
                            {editing ? (
                                <>
                                    <div className="flex items-center gap-1 mt-1">
                                        <span className="text-slate-400 font-mono text-sm">
                                            {window.location.origin}/u/
                                        </span>
                                        <input
                                            className="field-input"
                                            style={{ padding: "6px 10px", fontFamily: "JetBrains Mono, monospace" }}
                                            value={uname}
                                            onChange={(e) => setUname(e.target.value.toLowerCase())}
                                            data-testid="share-username-input"
                                        />
                                    </div>
                                    {error && (
                                        <p className="text-xs text-sciences mt-1" data-testid="share-username-error">{error}</p>
                                    )}
                                    <div className="flex gap-2 mt-2">
                                        <button
                                            disabled={saving}
                                            onClick={saveHandle}
                                            className="arcade-btn text-xs"
                                            data-variant="primary"
                                            data-testid="share-username-save"
                                            style={{ padding: "0.4rem 0.9rem" }}
                                        >
                                            <Check className="w-3 h-3" /> Save
                                        </button>
                                        <button
                                            onClick={() => {
                                                setEditing(false);
                                                setUname(user?.username || "");
                                                setError(null);
                                            }}
                                            className="arcade-btn text-xs"
                                            data-variant="ghost"
                                            style={{ padding: "0.4rem 0.9rem" }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="font-mono text-sm text-slate-900 truncate" data-testid="share-url">
                                        {publicUrl}
                                    </span>
                                    <button
                                        onClick={() => setEditing(true)}
                                        className="ml-auto text-slate-400 hover:text-slate-900"
                                        data-testid="share-edit-username"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex gap-2">
                            <button
                                onClick={copy}
                                className="arcade-btn flex-1"
                                data-variant="primary"
                                data-testid="share-copy-btn"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? "Copied" : "Copy link"}
                            </button>
                            <button
                                onClick={nativeShare}
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="share-native-btn"
                                title="Share…"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="mt-2 grid grid-cols-3 gap-2">
                            <button
                                onClick={tweetIntent}
                                className="arcade-btn"
                                data-variant="humanities"
                                data-testid="share-tweet-btn"
                                style={{ padding: "0.55rem 0.6rem", fontSize: "0.78rem" }}
                            >
                                <Twitter className="w-3 h-3" /> Post
                            </button>
                            <button
                                onClick={linkedInIntent}
                                className="arcade-btn"
                                data-variant="sciences"
                                data-testid="share-linkedin-btn"
                                style={{ padding: "0.55rem 0.6rem", fontSize: "0.78rem" }}
                            >
                                <Linkedin className="w-3 h-3" /> LinkedIn
                            </button>
                            <button
                                onClick={facebookIntent}
                                className="arcade-btn"
                                data-variant="trades"
                                data-testid="share-facebook-btn"
                                style={{ padding: "0.55rem 0.6rem", fontSize: "0.78rem" }}
                            >
                                <Facebook className="w-3 h-3" /> Facebook
                            </button>
                        </div>

                        <a
                            href={publicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 block text-center text-xs text-slate-500 hover:text-slate-900 font-display font-bold underline"
                            data-testid="share-preview-link"
                        >
                            Preview your public page →
                        </a>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
